import { type TypedDocumentNode, gql } from '@apollo/client';
import type {
  GetArticleDataQuery,
  GetArticleDataQueryVariables,
  GetDashboardDataQuery,
  GetDashboardDataQueryVariables,
  GetFeaturedMissionDataQuery,
  GetMissionModalDataQuery,
  GetMissionModalDataQueryVariables,
  GetMissionSectionDataQuery,
  GetMissionSectionDataQueryVariables,
} from '../../models/__generated__/graphql';
import { BADGE_BASICS } from './fragments/badge';
import {
  MISSION_BASICS,
  MISSION_STATUS,
} from './fragments/mission/missionBaseFragments';
import { MISSION_TASKS } from './fragments/mission/missionSubFragments';

export const GET_DASHBOARD_DATA: TypedDocumentNode<
  GetDashboardDataQuery,
  GetDashboardDataQueryVariables
> = gql`
  query GetDashboardData {
    me {
      __typename
      id
      badgesLink
      badges {
        ...BadgeBasics
      }
      articles(page: { pageSize: 3, pageNumber: 1 }) {
        totalCount
        items {
          id
          title
          thumbnailImageUrl
          thumbnailImageAltText
          subtitle
          contentURL
        }
      }
      level {
        __typename
        currentLevel
        currentPoints
        nextLevel
        nextLevelPoints
        progressPercentage
      }
    }
  }

  ${BADGE_BASICS}
`;

export const GET_ARTICLE_DATA: TypedDocumentNode<
  GetArticleDataQuery,
  GetArticleDataQueryVariables
> = gql`
  query GetArticleData($articleId: ID!) {
    me {
      id
      article(articleId: $articleId) {
        id
        title
        subtitle
        mainImageAltText
        mainImageUrl
        contentHTML
        startDate
      }
    }
  }
`;

export const GET_FEATURED_MISSION_DATA: TypedDocumentNode<GetFeaturedMissionDataQuery> = gql`
  query GetFeaturedMissionData {
    me {
      id
      missions(flag: FEATURED) {
        __typename
        ...MissionBasics
        ...MissionsStatus
        featured
        largeTileImageUrl
        expiringDate
        textColor
        lockedStatus {
          __typename
          locked
        }
      }
    }
  }

  ${MISSION_BASICS}
  ${MISSION_STATUS}
`;

export const GET_MISSIONS_SECTION_DATA: TypedDocumentNode<
  GetMissionSectionDataQuery,
  GetMissionSectionDataQueryVariables
> = gql`
  query GetMissionSectionData(
    $flag: MissionFlag!
    $labels: String
    $expiringSoon: Boolean
    $inProgress: Boolean
    $repeatable: Boolean
    $iLike: Boolean
  ) {
    me {
      __typename
      id
      points
      missions(
        flag: $flag
        labels: $labels
        expiringSoon: $expiringSoon
        inProgress: $inProgress
        repeatable: $repeatable
        iLike: $iLike
      ) {
        __typename
        featured
        ...MissionBasics
        ...MissionsStatus
      }
    }
  }

  ${MISSION_BASICS}
  ${MISSION_STATUS}
`;

export const GET_MISSION_MODAL_DATA: TypedDocumentNode<
  GetMissionModalDataQuery,
  GetMissionModalDataQueryVariables
> = gql`
  query GetMissionModalData($missionId: String!) {
    me {
      __typename
      id
      mission(missionId: $missionId) {
        __typename
        ...MissionBasics
        ...MissionsStatus
        ...MissionTasks
      }
    }
  }
  ${MISSION_BASICS}
  ${MISSION_STATUS}
  ${MISSION_TASKS}
`;
